import { useCallback, useEffect, useState } from "react";

import dayjs from "dayjs";
import { FormProvider, useForm } from "react-hook-form";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button, Link, Stack, Typography } from "@mui/material";

import { useOutletApi } from "@/features/authentication";
import {
  buildConvertPurchasePaymentDiscountAccountRows,
  CONVERT_PURCHASE_PAYMENT_DEFAULT_VALUES,
  CONVERT_PURCHASE_PAYMENT_VALIDATION_SCHEMA,
  ConvertPurchasePaymentDiscountAccountsSection,
  ConvertPurchasePaymentEditableFieldsSection,
  ConvertPurchasePaymentGeneralInformationSection,
  ConvertPurchasePaymentInvoicePaymentsSection,
  INCOMING_PURCHASE_PAYMENTS_CHANGED_EVENT,
} from "@/features/purchase-payments";
import { CenteredLoading, PageContainer, useSnackbar } from "@/features/ui";
import { getErrorMessage } from "@/features/shared";

export const CreateSalesPaymentFromPurchasePayment = () => {
  const { purchasePaymentReferenceNumber } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const api = useOutletApi();
  const [purchasePayment, setPurchasePayment] = useState(null);
  const [loading, setLoading] = useState(true);

  const methods = useForm({
    defaultValues: CONVERT_PURCHASE_PAYMENT_DEFAULT_VALUES,
    resolver: yupResolver(CONVERT_PURCHASE_PAYMENT_VALIDATION_SCHEMA),
  });

  const { handleSubmit, formState, reset } = methods;

  const loadPurchasePayment = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await api.get(
        `purchase-payments/incoming/${purchasePaymentReferenceNumber}`
      );
      setPurchasePayment(data);
      // The discount rows are fixed by the source, so they are seeded here
      // rather than being something the operator can add or remove.
      reset({
        ...CONVERT_PURCHASE_PAYMENT_DEFAULT_VALUES,
        discountAccounts: buildConvertPurchasePaymentDiscountAccountRows(
          data.purchaseInvoicePayments
        ),
      });
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat pembayaran pembelian"));
    } finally {
      setLoading(false);
    }
  }, [api, purchasePaymentReferenceNumber, reset, showSnackbar]);

  useEffect(() => {
    loadPurchasePayment();
  }, [loadPurchasePayment]);

  const onSubmit = async (formData) => {
    try {
      const payload = {
        transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
        note: formData.note?.trim() || null,
        cashBankOutletChartOfAccountId: formData.outletChartOfAccount.id,
        // Omitted entirely when the source carries no discounts — the backend
        // rejects the key being present in that case.
        ...(formData.discountAccounts.length > 0 && {
          discountAccounts: formData.discountAccounts.map((discountAccount) => ({
            purchaseInvoicePaymentDiscountOutletChartOfAccountId:
              discountAccount.purchaseInvoicePaymentDiscountOutletChartOfAccountId,
            outletChartOfAccountId: discountAccount.outletChartOfAccount.id,
          })),
        }),
      };

      const {
        data: { data },
      } = await api.post(
        `purchase-payments/${purchasePaymentReferenceNumber}/sales-payments`,
        payload
      );

      // This payment just left the queue; drop the drawer badge now rather than
      // at the next poll.
      window.dispatchEvent(new Event(INCOMING_PURCHASE_PAYMENTS_CHANGED_EVENT));

      showSnackbar(
        `Penerimaan penjualan ${data.referenceNumber} berhasil dibuat`,
        "success"
      );
      navigate(`/sales/sales-payments/${data.referenceNumber}`);
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Gagal menyimpan data")
      );
      throw error;
    }
  };

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (!purchasePayment) {
    return (
      <Typography variant="h6" color="error">
        Pembayaran pembelian tidak ditemukan
      </Typography>
    );
  }

  // A payment converts at most once. The endpoint still serves it afterwards so
  // the hub can see what it agreed to, so show the result instead of a form
  // that would only be rejected.
  const convertedSalesPayment = purchasePayment.convertedSalesPayment;

  return (
    <PageContainer
      maxWidth="xl"
      title="Buat Penerimaan Penjualan dari Pembayaran Pembelian"
      breadcrumbs={[
        {
          title: "Pembayaran dari Pembeli",
          path: "/sales/incoming-purchase-payments",
        },
        { title: purchasePaymentReferenceNumber },
        { title: "Buat Penerimaan Penjualan" },
      ]}
    >
      <Stack spacing={2}>
        {convertedSalesPayment ? (
          <Alert severity="success">
            Pembayaran ini sudah dikonversi menjadi{" "}
            <Link
              component={RouterLink}
              to={`/sales/sales-payments/${convertedSalesPayment.referenceNumber}`}
              underline="hover"
            >
              {convertedSalesPayment.referenceNumber}
            </Link>
            .
          </Alert>
        ) : (
          <Alert severity="info">
            Nilai penerimaan mengikuti pembayaran asal — alokasi, diskon, dan
            totalnya tidak dapat diubah. Yang ditentukan di sini hanya akun kas
            &amp; bank outlet ini beserta pemetaan akun diskonnya.
          </Alert>
        )}

        <ConvertPurchasePaymentGeneralInformationSection
          purchasePayment={purchasePayment}
        />

        <ConvertPurchasePaymentInvoicePaymentsSection
          purchaseInvoicePayments={purchasePayment.purchaseInvoicePayments ?? []}
        />

        {!convertedSalesPayment && (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack spacing={2}>
                <ConvertPurchasePaymentEditableFieldsSection />

                <ConvertPurchasePaymentDiscountAccountsSection />

                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    disabled={formState.isSubmitting}
                  >
                    Batal
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    loading={formState.isSubmitting}
                  >
                    Buat Penerimaan Penjualan
                  </Button>
                </Stack>
              </Stack>
            </form>
          </FormProvider>
        )}
      </Stack>
    </PageContainer>
  );
};
